import React, { ReactNode } from 'react';
import { AddContactsPeopleTableKeys, HEADER_OPTIONS } from './data';
import { Checkbox } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import { isEmail } from 'utils';
import classNames from 'classnames';
import { getIsValidPhone, getPhone } from 'phoneNumberUtils';
import ResolvePopup from 'components/ResolvePopup';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import BulkUploadSelect from 'components/BulkUploadSelect';

type CellProps = {
  column: string;
  contact: Record<string, any>;
  isPreApproved?: boolean;
  onPreApproveContact?: (contact: Record<string, any>) => void;
};

const ApprovedCell: React.FC<CellProps> = ({
  contact,
  isPreApproved,
  onPreApproveContact,
}) => (
  <div onClick={(e) => e.stopPropagation()}>
    <Checkbox
      checked={isPreApproved}
      onChange={() => onPreApproveContact(contact)}
    />
  </div>
);

const MobileCell: React.FC<CellProps> = ({ contact }) => {
  const { phoneNumber } = contact || {};

  if (!phoneNumber) return null;

  const phone = getPhone(phoneNumber);

  const isValid = getIsValidPhone(phone);

  return (
    <div className={classNames({ 'invalid-cell': !isValid })}>
      {phoneNumber.toString().includes('+') ? phoneNumber : `+${phoneNumber}`}
    </div>
  );
};

const EmailCell: React.FC<CellProps> = ({ contact }) => {
  const { email } = contact || {};

  const isInvalid = !isEmail(email);

  if (!email) return null;

  return (
    <div className={classNames({ 'invalid-cell': isInvalid })}>{email}</div>
  );
};

const FirstNameCell: React.FC<CellProps> = ({ contact }) => {
  const { firstName } = contact || {};

  return (
    <div className={classNames({ 'invalid-cell': !firstName })}>
      {firstName}
    </div>
  );
};

const LastNameCell: React.FC<CellProps> = ({ contact }) => {
  const { lastName } = contact || {};

  return (
    <div className={classNames({ 'invalid-cell': !lastName })}>{lastName}</div>
  );
};

export const KeyCell: React.FC<CellProps> = ({ column, contact }) => {
  const el = contact[column];

  return <div>{el}</div>;
};

export const BODY_CONTENT_GENERATE: Record<
  AddContactsPeopleTableKeys,
  (cellProps: CellProps) => ReactNode
> = {
  preApproved: (cellProps: CellProps) => <ApprovedCell {...cellProps} />,
  firstName: (cellProps: CellProps) => <FirstNameCell {...cellProps} />,
  lastName: (cellProps: CellProps) => <LastNameCell {...cellProps} />,
  email: (cellProps: CellProps) => <EmailCell {...cellProps} />,
  phoneNumber: (cellProps: CellProps) => <MobileCell {...cellProps} />,
  companyName: (cellProps: CellProps) => <KeyCell {...cellProps} />,
  title: (cellProps: CellProps) => <KeyCell {...cellProps} />,
};

type HeaderProps = {
  column: string;
  index?: number;
  allColumns: string[];
  areAllContactApproved?: boolean;
  onApproveAll?: () => void;
  onUpdateColumn?: (key: string, newValue: string) => void;
};

const ApprovedHeader: React.FC<HeaderProps> = ({
  areAllContactApproved,
  onApproveAll,
}) => {
  const { t } = useTranslation();

  return (
    <Checkbox
      checked={areAllContactApproved}
      onChange={onApproveAll}
      label={
        <div>
          {t('preApprove')}
          <ResolvePopup
            onHover
            preferUpwards
            popupContent={t('preApprovedDescription')}
          >
            <InformationcircleIcon />
          </ResolvePopup>
        </div>
      }
    />
  );
};

export const KeyHeader: React.FC<HeaderProps> = (props) => (
  <BulkUploadSelect {...props} options={HEADER_OPTIONS} />
);

export const HEADER_CONTENT_GENERATE: Record<
  AddContactsPeopleTableKeys,
  (headerProps: HeaderProps) => ReactNode
> = {
  preApproved: (headerProps: HeaderProps) => (
    <ApprovedHeader {...headerProps} />
  ),
  firstName: (headerProps: HeaderProps) => <KeyHeader {...headerProps} />,
  lastName: (headerProps: HeaderProps) => <KeyHeader {...headerProps} />,
  email: (headerProps: HeaderProps) => <KeyHeader {...headerProps} />,
  phoneNumber: (headerProps: HeaderProps) => <KeyHeader {...headerProps} />,
  companyName: (headerProps: HeaderProps) => <KeyHeader {...headerProps} />,
  title: (headerProps: HeaderProps) => <KeyHeader {...headerProps} />,
};
